import { useHybridOfferAdmin } from 'hooks/useHybridOfferAdmin'
import { useTranslation } from 'i18n'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import usePermissions from 'utils/hooks/use-permissions'
import useUserPlatform from 'utils/hooks/use-platform/useUserPlatform'
import { useSuperAutorEligible } from 'utils/hooks/use-super-autor-eligible/useSuperAutorEligible'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useAdminSidebarStore } from 'stores/adminSidebarStore'
import { FontIcon, tokens } from '@arvoreeducacao/bonsai-ds'
import ButtonGeneralSumary from './button-general-sumary'
import DropdownMenuItems from './dropdwon-menu-items'
import CollapsedSidebarItem from './collapsed-sidebar-item/CollapsedSidebarItem'
import {
  CollapseToggleButton,
  SidebarOverlay,
  SidebarV2Content,
  WrapperSidebar
} from './SidebarV2.styles'
import { sideBarItems } from './sideBarItems'
import { PanelLeft, PanelLeftClose } from 'lucide-react'

const SidebarV2 = () => {
  const { user } = useUserInfo()
  const { featureToggles } = useFeatureToggles()
  const { isSuperAutorEligible } = useSuperAutorEligible()
  const { canManagePermissions, canManageCollections } = usePermissions()
  const { isHybridOfferEnabled } = useHybridOfferAdmin()
  const { isMobile, windowWidth } = useUserPlatform()
  const location = useLocation()
  const { t } = useTranslation()
  const { isOpen, close, collapsed, toggleCollapsed } = useAdminSidebarStore()
  const [activeDropdownId, setActiveDropdownId] = useState<number[]>([0])
  const [searchQuery, setSearchQuery] = useState('')

  const hasTeacherId = Boolean(user?.teacher?.id)

  const isTutorialMode =
    new URLSearchParams(location.search).get('tutorial') === 'true'

  const isActivitiesStep =
    new URLSearchParams(location.search).get('activities-step') === 'true'

  const handleDropdownToggle = (dropdownId: number) => {
    setActiveDropdownId(prevId =>
      prevId.includes(dropdownId)
        ? prevId.filter(id => id !== dropdownId)
        : [dropdownId]
    )
  }

  const sidebarItems = sideBarItems({
    featureToggles,
    user,
    canManageCollections,
    canManagePermissions,
    hasTeacherId,
    isSuperAutorEligible,
    isHybridOfferEnabled,
    isMobile,
    t
  })

  useEffect(() => {
    if (isTutorialMode) {
      setActiveDropdownId([0, 1])
      return
    }
    if (isActivitiesStep) {
      setActiveDropdownId([2, 3])
      return
    }

    setActiveDropdownId([0])
  }, [isTutorialMode, isActivitiesStep])

  useEffect(() => {
    close()
  }, [location.pathname, close])

  const handleCollapsedItemClick = (dropdownId: number) => {
    toggleCollapsed()
    setActiveDropdownId([dropdownId])
  }

  const normalizedQuery = searchQuery.toLowerCase().trim()

  const filteredSidebarItems = useMemo(() => {
    if (!normalizedQuery) return sidebarItems
    return sidebarItems
      .map(section => ({
        ...section,
        items: section.items?.filter(item =>
          item.title.toLowerCase().includes(normalizedQuery)
        )
      }))
      .filter(
        section =>
          section.title.toLowerCase().includes(normalizedQuery) ||
          (section.items && section.items.length > 0)
      )
  }, [sidebarItems, normalizedQuery])

  const isDesktopView = windowWidth >= tokens.breakpoints.desktop

  return (
    <>
      <SidebarOverlay isOpen={isOpen} onClick={close} />
      <WrapperSidebar isOpen={isOpen} collapsed={isDesktopView && collapsed}>
        <SidebarV2Content>
          {isDesktopView && collapsed ? (
            <>
              <CollapseToggleButton
                onClick={toggleCollapsed}
                collapsed={true}
                aria-label="Expandir sidebar"
              >
                <PanelLeft size={16} color={tokens.colors.dark3} />
              </CollapseToggleButton>
              <div className="flex flex-col items-center gap-1">
                {featureToggles?.adminPanelV2Rollout && (
                  <CollapsedSidebarItem
                    iconName="chart-pie"
                    title={t('Resumo')}
                    path="/app-v2/admin/summary"
                  />
                )}
                {sidebarItems.map(item => (
                  <CollapsedSidebarItem
                    key={item.id}
                    iconName={item.iconName}
                    title={item.title}
                    items={item.items}
                    onExpand={() => handleCollapsedItemClick(item.id)}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 px-1 mb-2 ml-[2px]">
                <div className="relative flex-1 mt-[8px]">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2">
                    <FontIcon
                      iconName="search"
                      color={tokens.colors.dark3}
                      size={14}
                    />
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Buscar..."
                    className="w-full rounded-lg border-none bg-gray-700 h-9 pl-8 pr-8 text-sm text-gray-50 placeholder-gray-400 outline-none transition-colors focus:bg-gray-700/80"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-50"
                      type="button"
                    >
                      <FontIcon
                        iconName="cancel"
                        color={tokens.colors.dark3}
                        size={12}
                      />
                    </button>
                  )}
                </div>
                <CollapseToggleButton
                  onClick={toggleCollapsed}
                  collapsed={false}
                  aria-label="Recolher sidebar"
                >
                  <PanelLeftClose size={16} color={tokens.colors.dark3} />
                </CollapseToggleButton>
              </div>

              {featureToggles?.adminPanelV2Rollout && !searchQuery && (
                <ButtonGeneralSumary />
              )}

              <div id="sidebar-reports-section" className="flex flex-col gap-2">
                {filteredSidebarItems
                  .filter(item => [0, 1].includes(item.id))
                  .map(item => (
                    <div key={item.id}>
                      <DropdownMenuItems
                        title={item.title}
                        items={item.items}
                        iconName={item.iconName}
                        isActive={
                          searchQuery
                            ? true
                            : activeDropdownId.includes(item.id)
                        }
                        onToggle={() => handleDropdownToggle(item.id)}
                      />
                    </div>
                  ))}
              </div>
              <div
                id="sidebar-general-content-section"
                className="flex flex-col gap-2"
              >
                {filteredSidebarItems
                  .filter(item => ![0, 1].includes(item.id))
                  .map(item => (
                    <div key={item.id}>
                      <DropdownMenuItems
                        title={item.title}
                        items={item.items}
                        iconName={item.iconName}
                        isActive={
                          searchQuery
                            ? true
                            : activeDropdownId.includes(item.id)
                        }
                        onToggle={() => handleDropdownToggle(item.id)}
                      />
                    </div>
                  ))}
              </div>
            </>
          )}
        </SidebarV2Content>
      </WrapperSidebar>
    </>
  )
}

export default SidebarV2
